import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const LanguageSwitcher = ({ variant = 'navbar' }) => {
  const { i18n, t } = useTranslation();
  const { user, updateUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    {
      code: 'de', 
      name: 'Deutsch',
      flag: '🇩🇪',
      winFlag: 'DE',
      flagAlt: 'DE'
    },
    {
      code: 'en',
      name: 'English',
      flag: '🇺🇸', 
      winFlag: 'EN',
      flagAlt: 'EN'
    }
  ];

  // Detect Windows and mobile platforms more reliably
  const isWindows = () => {
    return navigator.platform.indexOf('Win') > -1 || navigator.userAgent.indexOf('Windows') > -1;
  };

  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Use simple logic: Windows gets text, mobile gets emoji
  const [useTextFlags] = useState(isWindows() && !isMobile());

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = async (languageCode) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
    // Save language preference to localStorage
    localStorage.setItem('selectedLanguage', languageCode);
    
    // If user is logged in, also save to their account
    if (user) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/update-profile`,
          { preferredLanguage: languageCode },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        // Update user in context with new language preference
        if (updateUser && response.data.user) {
          updateUser(response.data.user);
        }
      } catch (error) {
        console.error('Failed to save language preference to account:', error);
        // Don't show error to user as localStorage fallback still works
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Style variants for different backgrounds
  const getButtonStyles = () => {
    if (variant === 'admin') {
      return "flex items-center space-x-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:bg-gray-700 transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 rounded-md border border-gray-300 dark:border-gray-600";
    }
    // Default navbar style (dark background)
    return "flex items-center space-x-2 px-3 py-2 text-white hover:text-gray-200 transition duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-20 rounded-md";
  };

  const getFlagStyle = () => {
    if (variant === 'admin') {
      return "text-gray-600 dark:text-gray-400 sm:hidden";
    }
    return "text-gray-300 sm:hidden";
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={getButtonStyles()}
        aria-label="Change language"
      >
        {useTextFlags ? (
          // Windows PC - Use clear text badges
          <span className={`text-sm font-bold px-2 py-1 rounded ${
            variant === 'admin' 
              ? 'bg-gray-700 text-white' 
              : 'bg-blue-600 text-white'
          }`}>
            {currentLanguage.winFlag}
          </span>
        ) : (
          // Mobile/Mac - Use emoji flags
          <>
            <span className="text-xl" style={{ 
              fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, Segoe UI Symbol, Android Emoji, EmojiSymbols',
              fontSize: '20px',
              lineHeight: '1'
            }}>
              {currentLanguage.flag}
            </span>
            <span className={`text-xs font-bold ${getFlagStyle()}`}>
              {currentLanguage.flagAlt}
            </span>
          </>
        )}
        <span className="hidden sm:inline text-sm font-medium">{currentLanguage.name}</span>
        <svg 
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg dark:shadow-gray-900 ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="py-1" role="menu" aria-orientation="vertical">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                className={`${
                  currentLanguage.code === language.code
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    : 'text-gray-700 dark:text-gray-300'
                } group flex items-center w-full px-4 py-2 text-sm hover:bg-gray-100 dark:bg-gray-700 hover:text-gray-900 dark:text-gray-100 transition duration-150`}
                role="menuitem"
              >
                {useTextFlags ? (
                  // Windows PC - Use clear text badges  
                  <span className="mr-3 text-xs font-bold bg-gray-700 text-white px-2 py-1 rounded">
                    {language.winFlag}
                  </span>
                ) : (
                  // Mobile/Mac - Use emoji flags
                  <>
                    <span 
                      className="mr-3 text-xl" 
                      style={{ 
                        fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, Segoe UI Symbol, Android Emoji, EmojiSymbols',
                        fontSize: '18px',
                        lineHeight: '1'
                      }}
                    >
                      {language.flag}
                    </span>
                    <span className="mr-2 text-xs font-bold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">
                      {language.flagAlt}
                    </span>
                  </>
                )}
                <span className="font-medium">{language.name}</span>
                {currentLanguage.code === language.code && (
                  <svg className="ml-auto w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path 
                      fillRule="evenodd" 
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;